<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import WhatsAppTemplateGallery from 'dashboard/components-next/whatsapp/WhatsAppTemplateGallery.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
});

const emit = defineEmits(['sendMessage']);
const { t } = useI18n();
const store = useStore();
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);
const selectedTemplate = ref(null);
const isRefreshing = ref(false);

const whatsAppTemplateMessages = computed(() =>
  getFilteredWhatsAppTemplates.value(props.inboxId)
);

const handlePopoverShow = () => {
  selectedTemplate.value = null;
};
const handlePopoverHide = () => {
  selectedTemplate.value = null;
};
const handleBack = () => {
  selectedTemplate.value = null;
};
const refreshTemplates = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', props.inboxId);
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.PICKER.REFRESH_ERROR'));
  } finally {
    isRefreshing.value = false;
  }
};
const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};
</script>

<template>
  <Popover
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div v-if="!selectedTemplate" class="new-conversation-template-gallery">
        <WhatsAppTemplateGallery
          :templates="whatsAppTemplateMessages"
          :is-refreshing="isRefreshing"
          show-refresh
          @select="template => (selectedTemplate = template)"
          @refresh="refreshTemplates"
        />
      </div>
      <WhatsappTemplate
        v-else
        :template="selectedTemplate"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>

<style scoped>
.new-conversation-template-gallery {
  width: min(88vw, 64rem);
  padding: 1rem;
}
</style>
